import { useEffect, useMemo, useState, useCallback } from "react";
import apiClient from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Inbox, RefreshCw, Mail, Phone, MessageSquare, CheckCircle2 } from "lucide-react";

const SOURCE_COLOR = {
  contact:      "bg-blue-100 text-blue-800",
  pr:           "bg-purple-100 text-purple-800",
  student_pr:   "bg-fuchsia-100 text-fuchsia-800",
  consultation: "bg-emerald-100 text-emerald-800",
  skilled:      "bg-pink-100 text-pink-800",
  gs:           "bg-amber-100 text-amber-800",
  chat:         "bg-cyan-100 text-cyan-800",
  pathway:      "bg-indigo-100 text-indigo-800",
  referral:     "bg-orange-100 text-orange-800",
  agent_partner:"bg-teal-100 text-teal-800",
  rma_network:  "bg-slate-200 text-slate-800",
  g485_planner: "bg-lime-100 text-lime-800",
};

export default function AdminInbox() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unread: 0, by_source: {} });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (unreadOnly) params.set("unread_only", "true");
    if (source !== "all") params.set("source", source);
    if (q) params.set("q", q);
    Promise.all([
      apiClient.get(`/admin/inbox?${params}`),
      apiClient.get("/admin/inbox/counts"),
    ]).then(([a, b]) => {
      setItems(a.data);
      setCounts(b.data);
    }).finally(() => setLoading(false));
  }, [unreadOnly, source, q]);

  useEffect(load, [load]);

  const openItem = async (item) => {
    setSelected(item);
    setDetail(null);
    try {
      const r = await apiClient.get(`/admin/inbox/${item.source_key}/${item.id}`);
      setDetail(r.data);
      // Mark read locally
      setItems((arr) => arr.map((i) => (i.id === item.id ? { ...i, is_read: true } : i)));
      setCounts((c) => ({
        ...c,
        unread: Math.max(0, c.unread - (item.is_read ? 0 : 1)),
        by_source: {
          ...c.by_source,
          [item.source_key]: {
            ...c.by_source[item.source_key],
            unread: Math.max(0, (c.by_source[item.source_key]?.unread || 0) - (item.is_read ? 0 : 1)),
          },
        },
      }));
    } catch (e) { /* noop */ }
  };

  const grouped = useMemo(() => Object.entries(counts.by_source || {}), [counts]);

  return (
    <div className="p-6 md:p-8" data-testid="admin-inbox-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-[#29324A] flex items-center gap-3">
            <Inbox className="h-7 w-7 text-[#29324A]" /> All Leads &amp; Submissions
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {counts.total} total · <span className="text-rose-600 font-semibold">{counts.unread} unread</span> across all forms &amp; assessments
          </p>
        </div>
        <button onClick={load} className="text-sm text-[#29324A] hover:text-[#C43A89] inline-flex items-center gap-1.5" data-testid="inbox-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Email pipeline warning */}
      {counts.email && !counts.email.enabled && (
        <div data-testid="email-warning" className="mt-5 bg-rose-50 border-l-4 border-rose-500 p-4 flex items-start gap-3">
          <span className="text-rose-500 text-xl leading-none mt-0.5">⚠</span>
          <div className="text-[13px] text-rose-900 leading-relaxed">
            <strong className="font-bold">Email notifications are NOT being sent.</strong> Every submission below was captured to the database, but no email reached
            {" "}<span className="font-semibold">{counts.email.recipient || "yasmin@studynet.com.au"}</span> because <code className="bg-rose-100 px-1.5 py-0.5 text-[11px] rounded">RESEND_API_KEY</code> is missing on the server.
            Ask your developer or set it via the Emergent dashboard → Environment variables, then redeploy. Once set, future submissions will email automatically.
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input data-testid="inbox-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, subject…" className="pl-9 w-72" />
        </div>
        <label className="text-xs text-slate-600 inline-flex items-center gap-2">
          <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="inbox-unread-toggle" />
          Unread only
        </label>
      </div>

      {/* Source chips */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        <FilterChip active={source === "all"} onClick={() => setSource("all")} label="All sources" count={counts.total} unread={counts.unread} testId="filter-all" />
        {grouped.map(([key, info]) => (
          <FilterChip
            key={key}
            active={source === key}
            onClick={() => setSource(key)}
            label={info.label}
            count={info.total}
            unread={info.unread}
            testId={`filter-${key}`}
          />
        ))}
      </div>

      {/* List */}
      <div className="mt-6 bg-white border border-slate-200">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Inbox className="h-8 w-8 mx-auto mb-3 text-slate-300" />
            No submissions match.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => {
              const tone = SOURCE_COLOR[item.source_key] || "bg-slate-100 text-slate-700";
              return (
                <li
                  key={`${item.source_key}-${item.id}`}
                  data-testid={`inbox-row-${item.source_key}-${item.id}`}
                  onClick={() => openItem(item)}
                  className={`px-5 py-4 cursor-pointer transition-colors hover:bg-[#FAF7FC] ${item.is_read ? "" : "bg-rose-50/40"}`}
                >
                  <div className="flex items-start gap-4">
                    {!item.is_read && <span aria-hidden className="mt-2 inline-block h-2.5 w-2.5 rounded-full bg-[#C43A89] shrink-0" />}
                    {item.is_read && <span aria-hidden className="mt-2 inline-block h-2.5 w-2.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 ${tone}`}>{item.source_label}</span>
                        <span className={`font-display text-base text-[#29324A] truncate ${item.is_read ? "" : "font-bold"}`}>{item.name}</span>
                      </div>
                      <div className="mt-1 text-[13px] text-slate-600 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3 text-slate-400" /> {item.email}</span>
                        {item.phone !== "—" && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" /> {item.phone}</span>}
                      </div>
                      {item.subject !== "—" && (
                        <p className="mt-1.5 text-[13px] text-slate-700 truncate">{item.subject}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] text-slate-500">{new Date(item.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</div>
                      <div className="text-[10px] font-bold tracking-wider uppercase text-slate-400 mt-1">{item.status || "new"}</div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 ${SOURCE_COLOR[selected?.source_key] || ""}`}>{selected?.source_label}</span>
              {selected?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">Full client submission and system assessment details</DialogDescription>
          </DialogHeader>
          {detail ? (
            <SubmissionView source={selected?.source_key} data={detail} />
          ) : (
            <div className="p-6 text-center text-slate-500">Loading…</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, unread, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-xs font-medium px-3 py-1.5 border transition-colors inline-flex items-center gap-1.5 ${
        active ? "bg-[#29324A] border-[#29324A] text-white" : "bg-white border-slate-200 text-slate-700 hover:border-[#29324A]"
      }`}
    >
      {label}
      <span className={`text-[10px] ${active ? "text-[#C43A89]" : "text-slate-400"}`}>{count ?? 0}</span>
      {unread > 0 && (
        <span className={`text-[10px] font-bold rounded-full px-1.5 ${active ? "bg-[#C43A89] text-white" : "bg-[#C43A89]/15 text-[#C43A89]"}`}>{unread}</span>
      )}
    </button>
  );
}

function SubmissionView({ source, data }) {
  const profile = data.profile || data;
  const hasSystemOutput = !!(data.points || data.rankings || data.report || data.ai || (source === "chat" && data.messages));

  return (
    <div className="space-y-6">
      {/* Meta / compliance strip */}
      <div className="grid sm:grid-cols-3 gap-x-6 gap-y-2 text-sm bg-slate-50 border border-slate-200 p-3" data-testid="submission-meta">
        <Meta k="Submitted" v={data.created_at ? new Date(data.created_at).toLocaleString("en-AU", { dateStyle: "long", timeStyle: "short" }) : "—"} />
        <Meta k="Status" v={data.status || "new"} />
        <Meta k="Reference" v={data.reference_id || data.id || "—"} />
        {(data.consent_timestamp || data.consent_given || data.consent) && (
          <Meta k="Consent recorded" v={data.consent_timestamp ? new Date(data.consent_timestamp).toLocaleString("en-AU") : "Yes"} />
        )}
        {data.consent_ip && <Meta k="Consent IP" v={data.consent_ip} />}
      </div>

      {/* ── A · CLIENT SUBMISSION — every question & answer ── */}
      <div data-testid="client-submission-section">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="text-[10px] font-bold tracking-[0.18em] uppercase bg-[#29324A] text-white px-2.5 py-1">A · Client Submission</span>
          <span className="text-[11px] text-slate-500">Every answer exactly as provided by the client — stored unmodified</span>
        </div>
        <ClientAnswers source={source} data={data} profile={profile} />
      </div>

      {/* Free-text message (client-provided) */}
      {(data.message || data.summary) && (
        <div>
          <div className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-1.5">
            <MessageSquare className="inline h-3.5 w-3.5 mr-1" /> Client Message
          </div>
          <div className="bg-slate-50 border-l-4 border-[#29324A] p-4 text-[13.5px] leading-relaxed text-slate-700 whitespace-pre-line">
            {data.message || data.summary}
          </div>
        </div>
      )}

      {/* ── B · SYSTEM ASSESSMENT — generated output ── */}
      {hasSystemOutput && (
        <div className="flex items-center gap-2 pt-1 flex-wrap" data-testid="system-assessment-banner">
          <span className="text-[10px] font-bold tracking-[0.18em] uppercase bg-[#C43A89] text-white px-2.5 py-1">B · System Assessment</span>
          <span className="text-[11px] text-slate-500">Generated automatically and shown to the client — additional to (never replacing) the answers above</span>
        </div>
      )}

      {/* GS Builder generated responses */}
      {source === "gs" && data.ai?.gs_responses && Array.isArray(data.ai.gs_responses) && (
        <div className="bg-gradient-to-br from-[#FFF8E1] to-[#FFFDE7] border border-amber-200 p-5">
          <div className="text-xs font-bold tracking-wider text-amber-700 uppercase mb-3 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> Generated Statement (GS / SOP)
          </div>
          <div className="space-y-4">
            {data.ai.gs_responses.map((qa, i) => (
              <div key={i} className="bg-white border border-amber-100 p-4">
                <div className="text-[11px] font-bold tracking-wider text-amber-700 uppercase mb-2">Q{i + 1}: {qa.question}</div>
                <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-line">{qa.answer}</p>
              </div>
            ))}
          </div>
          {data.ai.summary && (
            <div className="mt-4 bg-white border border-amber-100 p-4">
              <div className="text-[11px] font-bold tracking-wider text-amber-700 uppercase mb-2">Summary</div>
              <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-line">{data.ai.summary}</p>
            </div>
          )}
        </div>
      )}

      {/* Submitted profile (GS) */}
      {source === "gs" && data.profile && (
        <div>
          <div className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-2">Submitted profile</div>
          <dl className="bg-slate-50 border border-slate-200 p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
            {Object.entries(data.profile).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] tracking-wider uppercase text-slate-400 font-bold">{k.replace(/_/g, " ")}</dt>
                <dd className="text-slate-800">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Parent visa rankings */}
      {source === "parent_visa" && Array.isArray(data.rankings) && data.rankings.length > 0 && (
        <div className="bg-gradient-to-br from-[#F8F4FB] to-[#FDF2F8] border border-[#C43A89]/20 p-5" data-testid="inbox-parent-visa-rankings">
          <div className="text-xs font-bold tracking-wider text-[#C43A89] uppercase mb-3 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> Parent Visa Pathway Report
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Top pathway" value={data.rankings[0]?.name || "—"} />
            <Stat label="Balance of Family" value={data.bof_test_result || "—"} />
            <Stat label="Aged Parent" value={data.aged_parent_eligible || "—"} />
          </div>
          <div className="mt-4">
            <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase mb-2">Top ranked visas</div>
            <div className="space-y-1.5">
              {data.rankings.slice(0, 5).map((r, i) => (
                <div key={r.code || i} className="flex items-center justify-between bg-white border border-slate-200 px-3 py-2 text-[12.5px]">
                  <span className="text-slate-700">#{i + 1} · Subclass {r.code} · <span className="text-slate-500">{r.name}</span></span>
                  <span className="font-display text-base text-[#29324A]">{r.score}<span className="text-xs text-slate-400">/100</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Skilled migration full report */}
      {source === "skilled" && data.points && (
        <div className="bg-gradient-to-br from-[#F8F4FB] to-[#FDF2F8] border border-[#C43A89]/20 p-5">
          <div className="text-xs font-bold tracking-wider text-[#C43A89] uppercase mb-3 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> Generated Pathway Report (as shown to client)
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Total points" value={data.points.total} />
            <Stat label="Readiness score" value={`${data.readiness?.score ?? "—"}/100`} />
            <Stat label="Band" value={data.readiness?.band || "—"} />
          </div>

          {/* Points basis — factor by factor */}
          <div className="mt-4" data-testid="points-basis">
            <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase mb-2">Points basis (how the total was calculated)</div>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {Object.entries(data.points).filter(([k]) => !["total", "breakdown"].includes(k)).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between bg-white border border-slate-200 px-3 py-1.5 text-[12.5px]">
                  <span className="text-slate-600">{humanize(k)}</span>
                  <span className="font-bold text-[#29324A]">{v} pts</span>
                </div>
              ))}
            </div>
            <p className="text-[11.5px] text-slate-500 mt-2 leading-relaxed">
              Each factor above is scored directly from the client&apos;s answers in Section A (age, English level, work experience, qualification, Australian/regional study, Professional Year, NAATI, partner and nomination answers).
            </p>
          </div>

          {Array.isArray(data.pathways) && data.pathways.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase mb-2">Pathway matches shown to client</div>
              <ul className="space-y-1.5">
                {data.pathways.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-slate-700">
                    <span className="text-[10px] font-bold tracking-wider uppercase bg-white px-2 py-0.5 border border-slate-200 shrink-0">{p.match}</span>
                    {p.name}
                  </li>
                ))}
              </ul>
              <p className="text-[11.5px] text-slate-500 mt-2 leading-relaxed" data-testid="pathway-reasoning">
                <strong>Reasoning:</strong> matches derive from the client&apos;s answers — total points ({data.points.total}), skills assessment outcome ({profile.skills_assessment_outcome || "not provided"}), regional willingness ({profile.regional_willing ? "Yes" : "No"}), employer sponsorship ({profile.employer_sponsor ? "Yes" : "No"}), currently in Australia ({profile.currently_in_australia ? "Yes" : "No"}), previous refusal ({profile.previous_refusal ? "Yes" : "No"}), Section 48 ({profile.section_48 ? "Yes" : "No"}).
              </p>
            </div>
          )}
          {Array.isArray(data.alternative) && data.alternative.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase mb-2">Alternative options shown</div>
              <ul className="space-y-1 text-[12.5px] text-slate-700 list-disc pl-5">
                {data.alternative.map((a, i) => <li key={i}><strong>{a.name}</strong>{a.reason ? ` — ${a.reason}` : ""}</li>)}
              </ul>
            </div>
          )}
          {data.exceptional && (
            <div className="mt-3 text-[12.5px] text-slate-700"><strong>Exceptional talent flag:</strong> {data.exceptional.name} — {data.exceptional.note}</div>
          )}
          {Array.isArray(data.risks) && data.risks.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase mb-2">Warnings / risk flags shown to client</div>
              <ul className="space-y-1 text-[12.5px] text-slate-700 list-disc pl-5">
                {data.risks.map((r, i) => <li key={i}><span className={`font-bold uppercase text-[10px] mr-1 ${r.level === "red" ? "text-red-600" : r.level === "yellow" ? "text-amber-600" : "text-emerald-600"}`}>{r.level}</span>{r.label || r.message || JSON.stringify(r)}</li>)}
              </ul>
            </div>
          )}
          {Array.isArray(data.action_plan) && data.action_plan.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] font-bold tracking-wider text-slate-500 uppercase mb-2">Action plan shown to client</div>
              <ol className="space-y-1 text-[12.5px] text-slate-700 list-decimal pl-5">
                {data.action_plan.map((a, i) => <li key={i}>{typeof a === "string" ? a : (a.text || JSON.stringify(a))}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* 485 planner report */}
      {source === "g485_planner" && data.report && (
        <div className="bg-gradient-to-br from-[#F8F4FB] to-[#FDF2F8] border border-[#C43A89]/20 p-5" data-testid="inbox-g485-report">
          <div className="text-xs font-bold tracking-wider text-[#C43A89] uppercase mb-3 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> 485 Pathway Report (as shown to client)
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Stream" value={data.report.stream_label || "—"} />
            <Stat label="Eligibility" value={data.report.eligibility_status || "—"} />
            <Stat label="Readiness" value={data.report.readiness_level || "—"} />
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px] text-slate-700">
            <div><strong>Time risk:</strong> {data.report.time_risk || "—"}</div>
            <div><strong>Sponsorship readiness:</strong> {data.report.sponsorship_readiness || "—"}</div>
            <div className="sm:col-span-2"><strong>Red flags:</strong> {(data.report.urgent_alerts || []).join("; ") || "None"}</div>
          </div>
        </div>
      )}

      {/* Chat history */}
      {source === "chat" && Array.isArray(data.messages) && (
        <div>
          <div className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-2">Conversation ({data.messages.length} messages)</div>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-2 bg-[#FAF7FC] border border-slate-200 p-3">
            {data.messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 text-[12.5px] leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-gradient-to-br from-[#29324A] to-[#C43A89] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                  {m.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded documents */}
      {Array.isArray(data.documents || data.docs || data.uploaded_files) && (data.documents || data.docs || data.uploaded_files).length > 0 && (
        <div>
          <div className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-2">Uploaded files</div>
          <ul className="space-y-1.5">
            {(data.documents || data.docs || data.uploaded_files).map((f, i) => (
              <li key={i} className="text-[12.5px] text-slate-700 flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                {f.name || f.original_filename || f.filename || JSON.stringify(f)}
                {f.id && <span className="text-[10px] text-slate-400">({f.id})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 p-3">
      <div className="text-[10px] tracking-wider uppercase text-slate-500 font-bold">{label}</div>
      <div className="font-display text-2xl text-[#29324A] mt-0.5">{value}</div>
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">{k}</div>
      <div className="text-[12.5px] text-slate-800 break-words">{String(v)}</div>
    </div>
  );
}

const LABEL_OVERRIDES = {
  anzsco: "ANZSCO code", naati: "NAATI credential", dob: "Date of birth",
  english_l: "English — Listening", english_r: "English — Reading", english_s: "English — Speaking", english_w: "English — Writing",
  english_overall: "English — Overall", cv_filename: "CV / Resume uploaded", iaa: "IAA",
};
export function humanize(k) {
  if (LABEL_OVERRIDES[k]) return LABEL_OVERRIDES[k];
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const SYSTEM_KEYS = new Set([
  "id", "_id", "is_read", "status", "created_at", "updated_at", "points", "pathways", "alternative",
  "exceptional", "risks", "action_plan", "readiness", "rankings", "report", "messages", "ai",
  "documents", "docs", "uploaded_files", "profile", "reference_id", "pathway_label", "source", "type",
  "reviewed_by", "consent", "consent_given", "consent_timestamp", "consent_ip", "consent_user_agent",
  "disclaimer_general", "disclaimer_no_guarantee", "session_id", "email_sent", "message", "summary",
  "readiness_score", "cv_file_path", "bof_test_result", "aged_parent_eligible",
]);

const SECTION_MAPS = {
  skilled: [
    ["Personal Details", ["full_name", "email", "phone", "dob", "age", "country_citizenship", "country_residence", "marital", "single", "dependants", "contact_pref", "location"]],
    ["Immigration Status", ["currently_in_australia", "current_visa", "visa_expiry", "previous_refusal", "previous_cancel", "section_48", "previous_eoi", "previous_invite", "currently_491_or_494"]],
    ["Occupation & Skills Assessment", ["current_occupation", "preferred_occupation", "anzsco", "assessing_authority", "skills_assessment_status", "skills_assessment_outcome", "assessment_expiry"]],
    ["Education", ["qualification", "qual_country", "qual_related", "course", "institution", "completion_year", "australian_study", "regional_study", "aus_graduate", "recent_graduate", "professional_year", "stem_research"]],
    ["Employment & Experience", ["employment_status", "total_years", "overseas_years", "australian_years", "current_employer", "currently_employed_au", "years_with_employer", "salary"]],
    ["English", ["english_level", "english_test", "english_overall", "english_l", "english_r", "english_s", "english_w"]],
    ["Additional Points", ["naati", "family_regional", "evidence"]],
    ["Partner", ["partner_age", "partner_occupation", "partner_skilled", "partner_english", "partner_aus_pr"]],
    ["State Nomination & Regional", ["state_nomination", "states_interested", "regional_willing", "regional_nomination"]],
    ["Employer Sponsorship", ["employer_sponsor", "job_offer", "sponsor_occupation", "training_pathway"]],
  ],
  student_pr: [
    ["Personal Details", ["full_name", "email", "phone", "date_of_birth", "nationality", "passport_number", "marital_status", "partner_details"]],
    ["Current Visa", ["current_visa_subclass", "visa_grant_date", "visa_expiry_date", "visa_conditions", "previous_visas"]],
    ["Education in Australia", ["current_course", "institution_name", "course_dates", "two_academic_years", "regional_study", "completed_principal_course", "incomplete_reason"]],
    ["Education History", ["highest_qualification", "full_study_history", "study_gaps"]],
    ["Employment & Experience", ["current_employment", "previous_employment", "employment_gaps", "job_related_to_study", "currently_employed", "employer_name"]],
    ["Skills & Occupation", ["intended_occupation", "occupation_on_skilled_list", "skills_assessment_completed", "skills_assessment_details"]],
    ["English", ["english_test_type", "english_test_score", "english_test_date"]],
    ["Points Factors", ["age", "naati_credentialed", "partner_skills", "additional_points"]],
    ["Sponsorship & Location", ["employer_willing_to_sponsor", "preferred_state", "interested_regional"]],
    ["Immigration History", ["visa_refusals", "visa_cancellations", "breaches_overstays", "warnings_notices"]],
    ["Australian Journey", ["first_arrival", "all_courses_in_australia", "course_institution_changes", "journey_explanation"]],
    ["Future Plans", ["preferred_pr_pathway", "planned_timeline", "career_goals"]],
    ["Documents", ["cv_filename"]],
  ],
};

function fmtVal(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (Array.isArray(v)) return v.length ? v.map((x) => (typeof x === "object" ? (x.name || x.filename || JSON.stringify(x)) : String(x))).join(", ") : "—";
  if (v == null || v === "") return "—";
  return String(v);
}

function AnswerGrid({ entries }) {
  return (
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
      {entries.map(([k, v]) => (
        <div key={k} className="py-1 border-b border-slate-100">
          <div className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">{humanize(k)}</div>
          <div className={`text-[13px] break-words whitespace-pre-line ${fmtVal(v) === "—" ? "text-slate-300" : "text-slate-800"}`}>{fmtVal(v)}</div>
        </div>
      ))}
    </div>
  );
}

function ClientAnswers({ source, data, profile }) {
  const src = data.profile ? profile : data;
  const map = SECTION_MAPS[source];
  const shown = new Set();
  const sections = [];

  if (map) {
    map.forEach(([title, keys]) => {
      const entries = keys.map((k) => { shown.add(k); return [k, src[k]]; });
      if (entries.length) sections.push([title, entries]);
    });
  }
  const leftover = Object.entries(src).filter(([k, v]) => !shown.has(k) && !SYSTEM_KEYS.has(k) && (typeof v !== "object" || Array.isArray(v)));
  if (leftover.length) sections.push([map ? "Other Answers" : "All Submitted Answers", leftover]);

  return (
    <div className="space-y-4" data-testid="client-answers">
      {sections.map(([title, entries]) => (
        <div key={title} className="bg-white border border-slate-200">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-bold tracking-[0.14em] text-[#29324A] uppercase">{title}</div>
          <div className="p-4"><AnswerGrid entries={entries} /></div>
        </div>
      ))}
    </div>
  );
}
